import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { payments } from '@lawbee/domain';
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  EmptyState,
  ErrorState,
  Field,
  Input,
  LoadingState,
  Select,
  Status,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
  cn,
  formatDate,
  formatDateTime,
  formatMoney,
} from '@lawbee/ui';
import { FileQuestion, Mail, MessageSquare, Plus, Receipt, Send, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { PlanGate } from '../../../components/PlanGate';
import { api, apiUrl } from '../../../lib/api';
import { humanize, todayIn, useApiMutation } from '../../../lib/hooks';
import { can, hasFeature, useMe } from '../../../lib/me';
import type { CaseUpdate, DocumentRequest, MatterDetail } from '../../../lib/types';
import { NewInvoiceDialog } from './invoice-dialog';
import { Panel, UPDATE_STATUS_LABELS, updateStatusTone, useMatterInvalidate } from './shared';

// ---------------------------------------------------------------------------
// Updates
// ---------------------------------------------------------------------------

export function UpdateHistoryItem({ update, showMatter }: { update: CaseUpdate; showMatter?: boolean }) {
  const [expanded, setExpanded] = useState(false);
  const long = update.body.length > 280;
  return (
    <li className="rounded-xl bg-card p-4 shadow-card">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <Status tone={updateStatusTone(update.status)}>{UPDATE_STATUS_LABELS[update.status] ?? humanize(update.status)}</Status>
        <Badge variant="outline">
          {update.channel === 'sms' ? <MessageSquare className="size-3" aria-hidden /> : <Mail className="size-3" aria-hidden />}
          {update.channel === 'sms' ? 'SMS' : 'Email'}
        </Badge>
        {update.aiDrafted ? (
          <Badge variant="primary">
            <Sparkles className="size-3" aria-hidden /> AI drafted
          </Badge>
        ) : null}
        <span className="figures ml-auto text-[12px] text-muted-foreground">{formatDateTime(update.sentAt ?? update.createdAt)}</span>
      </div>
      {showMatter && update.matterNumber ? (
        <p className="mt-2 text-[13px] text-muted-foreground">
          <Link to="/matters/$id" params={{ id: update.matterId }} className="hover:text-foreground hover:underline">
            <span className="figures">{update.matterNumber}</span> {update.matterTitle}
          </Link>
          {update.clientName ? ` · ${update.clientName}` : ''}
        </p>
      ) : null}
      {update.subject ? <p className="mt-2 font-medium">{update.subject}</p> : null}
      <p className={cn('mt-1 whitespace-pre-line text-sm text-muted-foreground', !expanded && long && 'line-clamp-4')}>{update.body}</p>
      {long ? (
        <Button variant="link" size="sm" className="h-auto px-0 py-1" onClick={() => setExpanded(!expanded)}>
          {expanded ? 'Show less' : 'Show more'}
        </Button>
      ) : null}
      {update.guardrailFlags.length > 0 ? <p className="mt-2 text-[12px] text-destructive">Flagged: {update.guardrailFlags.join(', ')}</p> : null}
    </li>
  );
}

export function UpdatesTab({ data }: { data: MatterDetail }) {
  const me = useMe();
  const [drafting, setDrafting] = useState(false);
  const [writing, setWriting] = useState(false);
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          {me.settings.updateMode === 'auto' ? 'Stage updates go to the client automatically.' : 'Stage updates wait in the approval queue before they go out.'}{' '}
          <Link to="/updates" className="font-medium text-foreground underline-offset-4 hover:underline">
            Approval queue
          </Link>
        </p>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setWriting(true)}>
            Write update
          </Button>
          <Button onClick={() => setDrafting(true)}>
            <Sparkles aria-hidden /> Draft update
          </Button>
        </div>
      </div>
      {data.updates.length === 0 ? (
        <EmptyState icon={<Send />} title="No client updates yet" description="Updates are drafted when the matter moves to a stage that notifies the client, or whenever you write one." />
      ) : (
        <ul className="space-y-3">
          {data.updates.map((u) => (
            <UpdateHistoryItem key={u.id} update={u} />
          ))}
        </ul>
      )}
      <DraftUpdateDialog open={drafting} onOpenChange={setDrafting} data={data} />
      <WriteUpdateDialog open={writing} onOpenChange={setWriting} data={data} />
    </div>
  );
}

function ChannelSelect({ id, value, onChange }: { id: string; value: 'email' | 'sms'; onChange: (v: 'email' | 'sms') => void }) {
  const me = useMe();
  const sms = hasFeature(me, 'sms_updates');
  return (
    <Field label="Channel" htmlFor={id} description={sms ? 'SMS reaches clients who opted in to text messages.' : 'SMS updates are part of LawBee Growth.'}>
      <Select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value as 'email' | 'sms')}
        options={[
          { value: 'email', label: 'Email' },
          { value: 'sms', label: 'SMS', disabled: !sms },
        ]}
      />
    </Field>
  );
}

function DraftUpdateDialog({ open, onOpenChange, data }: { open: boolean; onOpenChange: (o: boolean) => void; data: MatterDetail }) {
  const me = useMe();
  const invalidate = useMatterInvalidate(data.matter.id);
  const qc = useQueryClient();
  const aiOn = me.settings.aiEnabled;
  const [useAi, setUseAi] = useState(aiOn);
  const [channel, setChannel] = useState<'email' | 'sms'>('email');
  const [draft, setDraft] = useState<CaseUpdate | null>(null);
  const create = useApiMutation(() => api.post<{ update: CaseUpdate }>('/api/updates/draft', { matterId: data.matter.id, useAi: aiOn && useAi, channel }), {
    success: 'Draft ready for review',
    errorTitle: 'Could not draft the update',
    onSuccess: (res) => {
      setDraft(res.update);
      void invalidate();
      void qc.invalidateQueries({ queryKey: ['updates'] });
    },
  });
  const close = (o: boolean) => {
    onOpenChange(o);
    if (!o) setDraft(null);
  };
  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        size="lg"
        title="Draft a client update"
        description="LawBee writes a short status update from the current stage and recent activity. Nothing is sent until it is approved."
        footer={
          draft ? (
            <>
              <Button variant="outline" onClick={() => close(false)}>
                Close
              </Button>
              <Button asChild>
                <Link to="/updates">Review in approval queue</Link>
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => close(false)}>
                Cancel
              </Button>
              <Button loading={create.isPending} onClick={() => create.mutate(undefined)}>
                Draft update
              </Button>
            </>
          )
        }
      >
        {draft ? (
          <div className="space-y-3">
            <Alert variant="success" title="Draft saved for approval" />
            {draft.subject ? <p className="font-medium">{draft.subject}</p> : null}
            <p className="whitespace-pre-line rounded-lg bg-muted p-3 text-sm">{draft.body}</p>
            {draft.guardrailFlags.length > 0 ? (
              <Alert variant="warning" title="Check the wording">
                This draft may read as legal advice: {draft.guardrailFlags.join(', ')}. Edit it in the approval queue.
              </Alert>
            ) : null}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-1">
              <Checkbox label="Use AI to write the draft" checked={aiOn && useAi} disabled={!aiOn} onCheckedChange={(c) => setUseAi(c === true)} />
              <p className="pl-[26px] text-xs text-muted-foreground">
                {aiOn ? 'AI drafts are screened so they never read as legal advice.' : 'AI is off for this firm. An owner can turn it on in Settings. The draft uses your stage template instead.'}
              </p>
            </div>
            <ChannelSelect id="draft-channel" value={channel} onChange={setChannel} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function WriteUpdateDialog({ open, onOpenChange, data }: { open: boolean; onOpenChange: (o: boolean) => void; data: MatterDetail }) {
  const me = useMe();
  const invalidate = useMatterInvalidate(data.matter.id);
  const qc = useQueryClient();
  const canSend = can(me, 'approve_updates');
  const [channel, setChannel] = useState<'email' | 'sms'>('email');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sendNow, setSendNow] = useState(canSend);
  const create = useApiMutation(
    () => api.post<{ update: CaseUpdate }>('/api/updates', { matterId: data.matter.id, channel, subject: channel === 'email' ? subject.trim() || null : null, body: body.trim(), sendNow: canSend && sendNow }),
    {
      success: (res) => (res.update.status === 'sent' ? 'Update sent' : res.update.status === 'failed' ? 'Saved, but it could not be delivered' : 'Update saved for approval'),
      errorTitle: 'Could not save the update',
      onSuccess: () => {
        setSubject('');
        setBody('');
        onOpenChange(false);
        void invalidate();
        void qc.invalidateQueries({ queryKey: ['updates'] });
      },
    },
  );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        size="lg"
        title="Write a client update"
        footer={
          <>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" form="write-update" loading={create.isPending} disabled={!body.trim()}>
              {canSend && sendNow ? 'Send update' : 'Save for approval'}
            </Button>
          </>
        }
      >
        <form
          id="write-update"
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (body.trim()) create.mutate(undefined);
          }}
        >
          <ChannelSelect id="write-channel" value={channel} onChange={setChannel} />
          {channel === 'email' ? (
            <Field label="Subject" htmlFor="write-subject">
              <Input id="write-subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder={`Update on ${data.matter.title}`} />
            </Field>
          ) : null}
          <Field label="Message" htmlFor="write-body" required description={channel === 'sms' ? `${body.length} characters` : undefined}>
            <Textarea id="write-body" rows={channel === 'sms' ? 4 : 8} value={body} onChange={(e) => setBody(e.target.value)} />
          </Field>
          {canSend ? (
            <Checkbox label="Send now (skip the approval queue)" checked={sendNow} onCheckedChange={(c) => setSendNow(c === true)} />
          ) : (
            <p className="text-xs text-muted-foreground">A lawyer or owner approves the update before it is sent.</p>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Document requests (Growth)
// ---------------------------------------------------------------------------

const REQUEST_STATUS: Record<string, { label: string; variant: 'warning' | 'success' | 'default' }> = {
  open: { label: 'Waiting on client', variant: 'warning' },
  received: { label: 'Received', variant: 'success' },
  cancelled: { label: 'Cancelled', variant: 'default' },
};

export function DocumentRequestsTab({ data }: { data: MatterDetail }) {
  return (
    <PlanGate feature="document_requests">
      <DocumentRequests data={data} />
    </PlanGate>
  );
}

function DocumentRequests({ data }: { data: MatterDetail }) {
  const me = useMe();
  const id = data.matter.id;
  const today = todayIn(me.workspace.timezone);
  const invalidate = useMatterInvalidate(id);
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['document-requests', id],
    queryFn: () => api.get<{ requests: DocumentRequest[] }>(`/api/document-requests?matterId=${encodeURIComponent(id)}`),
  });
  const refresh = () => Promise.all([invalidate(), qc.invalidateQueries({ queryKey: ['document-requests', id] })]);
  const [creating, setCreating] = useState(false);
  const setStatus = useApiMutation((args: { rid: string; status: 'open' | 'received' | 'cancelled' }) => api.patch(`/api/document-requests/${args.rid}`, { status: args.status }), {
    success: (_d) => 'Request updated',
    onSuccess: () => void refresh(),
  });
  const nudge = useApiMutation((rid: string) => api.post(`/api/document-requests/${rid}/nudge`), {
    success: 'Reminder sent to the client',
    errorTitle: 'Could not send a reminder',
    onSuccess: () => void refresh(),
  });
  const requests = q.data?.requests ?? data.documentRequests;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">Ask the client for documents. They upload them in the portal or reply by email.</p>
        <Button onClick={() => setCreating(true)}>
          <Plus aria-hidden /> Request a document
        </Button>
      </div>
      {q.isLoading && data.documentRequests.length === 0 ? (
        <LoadingState label="Loading requests" />
      ) : q.error && data.documentRequests.length === 0 ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      ) : requests.length === 0 ? (
        <EmptyState icon={<FileQuestion />} title="No document requests" description="Request ID, financial statements, signed forms and more. LawBee reminds the client until it arrives." />
      ) : (
        <ul className="space-y-3">
          {requests.map((r) => {
            const st = REQUEST_STATUS[r.status] ?? { label: humanize(r.status), variant: 'default' as const };
            const open = r.status === 'open';
            const overdue = open && r.dueOn && r.dueOn < today;
            return (
              <li key={r.id} className="rounded-xl bg-card p-4 shadow-card">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium">{r.title}</p>
                    {r.description ? <p className="mt-0.5 whitespace-pre-line text-sm text-muted-foreground">{r.description}</p> : null}
                    <p className="mt-1.5 flex flex-wrap gap-x-3 text-[12px] text-muted-foreground">
                      <span>
                        Requested <span className="figures">{formatDate(r.createdAt)}</span>
                      </span>
                      {r.dueOn ? (
                        <span className={cn(overdue && 'text-destructive')}>
                          Due <span className="figures">{formatDate(r.dueOn)}</span>
                        </span>
                      ) : null}
                      {r.lastNudgedAt ? (
                        <span>
                          Last reminder <span className="figures">{formatDateTime(r.lastNudgedAt)}</span>
                        </span>
                      ) : null}
                    </p>
                  </div>
                  <Badge variant={overdue ? 'destructive' : st.variant}>{overdue ? 'Overdue' : st.label}</Badge>
                </div>
                {r.documents && r.documents.length > 0 ? (
                  <ul className="mt-3 flex flex-wrap gap-2">
                    {r.documents.map((d) => (
                      <li key={d.id}>
                        <a href={apiUrl(`/api/matters/${id}/documents/${d.id}`)} download className="inline-flex items-center rounded-lg bg-muted px-2.5 py-1 text-[13px] hover:underline">
                          {d.name}
                        </a>
                      </li>
                    ))}
                  </ul>
                ) : null}
                {open ? (
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => setStatus.mutate({ rid: r.id, status: 'received' })} disabled={setStatus.isPending}>
                      Mark received
                    </Button>
                    <Button size="sm" variant="outline" loading={nudge.isPending && nudge.variables === r.id} onClick={() => nudge.mutate(r.id)} disabled={!data.matter.client?.email}>
                      Nudge client
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setStatus.mutate({ rid: r.id, status: 'cancelled' })} disabled={setStatus.isPending}>
                      Cancel request
                    </Button>
                  </div>
                ) : (
                  <div className="mt-3">
                    <Button size="sm" variant="ghost" onClick={() => setStatus.mutate({ rid: r.id, status: 'open' })} disabled={setStatus.isPending}>
                      Reopen
                    </Button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
      <NewDocumentRequestDialog open={creating} onOpenChange={setCreating} data={data} onCreated={() => void refresh()} />
    </div>
  );
}

function NewDocumentRequestDialog({ open, onOpenChange, data, onCreated }: { open: boolean; onOpenChange: (o: boolean) => void; data: MatterDetail; onCreated: () => void }) {
  const hasEmail = !!data.matter.client?.email;
  const [v, setV] = useState({ title: '', description: '', dueOn: '', notify: true });
  const create = useApiMutation(
    () => api.post('/api/document-requests', { matterId: data.matter.id, title: v.title.trim(), description: v.description.trim() || null, dueOn: v.dueOn || null, notify: hasEmail && v.notify }),
    {
      success: hasEmail && v.notify ? 'Request sent to the client' : 'Request created',
      onSuccess: () => {
        setV({ title: '', description: '', dueOn: '', notify: true });
        onOpenChange(false);
        onCreated();
      },
    },
  );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title="Request a document"
        footer={
          <>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" form="new-docreq" loading={create.isPending} disabled={v.title.trim().length < 2}>
              Create request
            </Button>
          </>
        }
      >
        <form
          id="new-docreq"
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (v.title.trim().length >= 2) create.mutate(undefined);
          }}
        >
          <Field label="What do you need?" htmlFor="dr-title" required>
            <Input id="dr-title" value={v.title} onChange={(e) => setV({ ...v, title: e.target.value })} placeholder="Last three years of tax returns" />
          </Field>
          <Field label="Details for the client" htmlFor="dr-desc">
            <Textarea id="dr-desc" rows={3} value={v.description} onChange={(e) => setV({ ...v, description: e.target.value })} />
          </Field>
          <Field label="Needed by" htmlFor="dr-due">
            <Input id="dr-due" type="date" className="figures" value={v.dueOn} onChange={(e) => setV({ ...v, dueOn: e.target.value })} />
          </Field>
          <div className="space-y-1">
            <Checkbox label="Email the client now" checked={hasEmail && v.notify} disabled={!hasEmail} onCheckedChange={(c) => setV({ ...v, notify: c === true })} />
            {!hasEmail ? <p className="pl-[26px] text-xs text-muted-foreground">The client has no email address on file.</p> : null}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Invoices
// ---------------------------------------------------------------------------

const INVOICE_STATUS_VARIANT: Record<string, 'success' | 'warning' | 'default' | 'info' | 'destructive'> = {
  paid: 'success',
  partially_paid: 'warning',
  partial: 'warning',
  sent: 'info',
  open: 'info',
  draft: 'default',
  void: 'default',
  failed: 'destructive',
};

const SHORT_KIND: Record<string, string> = { retainer: 'Retainer', invoice: 'Invoice', consultation_fee: 'Consultation fee' };

export function InvoicesTab({ data }: { data: MatterDetail }) {
  const [creating, setCreating] = useState(false);
  const total = data.invoices.filter((i) => i.status !== 'void').reduce((s, i) => s + i.amountCents, 0);
  const paid = data.invoices.filter((i) => i.status !== 'void').reduce((s, i) => s + i.paidCents, 0);
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-6 text-sm">
          <div>
            <div className="figures text-lg font-semibold">{formatMoney(total)}</div>
            <div className="text-[12px] text-muted-foreground">Billed</div>
          </div>
          <div>
            <div className="figures text-lg font-semibold">{formatMoney(total - paid)}</div>
            <div className="text-[12px] text-muted-foreground">Outstanding</div>
          </div>
        </div>
        <Button onClick={() => setCreating(true)} disabled={data.matter.status === 'closed'}>
          <Plus aria-hidden /> New invoice
        </Button>
      </div>
      <Panel title="Invoices" contentClassName="px-0 sm:px-6">
        {data.invoices.length === 0 ? (
          <EmptyState className="py-8" icon={<Receipt />} title="No invoices" description="Request a retainer or bill fees. The client pays online by card." />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Number</TableHead>
                <TableHead>Kind</TableHead>
                <TableHead>Goes to</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead className="text-right">Paid</TableHead>
                <TableHead>Due</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.invoices.map((inv) => (
                <TableRow key={inv.id}>
                  <TableCell className="whitespace-nowrap">
                    <span className="figures font-medium">{inv.number}</span>
                    <div className="max-w-56 truncate text-[12px] text-muted-foreground">{inv.description}</div>
                  </TableCell>
                  <TableCell className="whitespace-nowrap">{SHORT_KIND[inv.kind] ?? humanize(inv.kind)}</TableCell>
                  <TableCell>{inv.destination === 'trust' ? <Badge variant="primary">Trust</Badge> : <Badge variant="outline">Operating</Badge>}</TableCell>
                  <TableCell className="figures whitespace-nowrap text-right">{formatMoney(inv.amountCents)}</TableCell>
                  <TableCell className="figures whitespace-nowrap text-right">{formatMoney(inv.paidCents)}</TableCell>
                  <TableCell className="figures whitespace-nowrap">{inv.dueOn ? formatDate(inv.dueOn) : '—'}</TableCell>
                  <TableCell>
                    <Badge variant={INVOICE_STATUS_VARIANT[inv.status] ?? 'default'}>{humanize(inv.status)}</Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Panel>
      <p className="text-[12px] leading-relaxed text-muted-foreground">{payments.TRUST_COMPLIANCE_NOTICE}</p>
      <NewInvoiceDialog open={creating} onOpenChange={setCreating} data={data} />
    </div>
  );
}
